/**
 * The context a DraggableCardGrid hands each card (see `card-drag-handle.tsx`),
 * kept in its own module so the component file exports components only.
 */

import { createContext, useContext, useLayoutEffect } from 'react'

export type CardControlsValue = {
  /** True when the card is inside a DraggableCardGrid (drag affordance shown). */
  inGrid: boolean
  /** Hide this card, when the grid supports hiding. Undefined = not hideable. */
  onHide?: () => void
  /** Card title, used for the hide control's accessible label. */
  title?: string
  /** WPS-146: the card tells the grid when it is folded to its header, so the
   *  grid can seat short cards beside each other. Undefined outside a grid,
   *  and for a card nested inside another card. */
  onFoldedChange?: (folded: boolean) => void
}

export const CardDragHandleContext = createContext<CardControlsValue>({ inGrid: false })

/**
 * WPS-146: report a card's fold to the grid around it (no-op outside a grid).
 * Call it from the card's own root; wrap the card's body in
 * `NestedCardBoundary` so a card nested inside it cannot report for it.
 */
export function useReportCardFolded(folded: boolean) {
  const { onFoldedChange } = useContext(CardDragHandleContext)
  // Before paint, so a card saved folded is seated with the folded cards on
  // the first frame rather than jumping into place a frame later.
  useLayoutEffect(() => {
    if (!onFoldedChange) return
    onFoldedChange(folded)
    return () => onFoldedChange(false)
  }, [folded, onFoldedChange])
}

